import React, { useState, useEffect } from 'react';
import {
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Box,
  Card,
  CardBody,
  CardHeader,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import {
  CalendarIcon,
  AddIcon,
  ChevronDownIcon,
  SearchIcon,
  EditIcon,
  DeleteIcon,
  ChevronRightIcon,
  TimeIcon,
  CheckCircleIcon,
  WarningIcon
} from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const Scheduling = () => {
  const [scheduledPublications, setScheduledPublications] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity_type: '',
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  const [cancellingId, setCancellingId] = useState(null);
  const { isOpen: isCancelDialogOpen, onOpen: onCancelDialogOpen, onClose: onCancelDialogClose } = useDisclosure();
  const cancelRef = React.useRef();

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (tenantId) {
      fetchScheduledPublications();
      fetchStats();
    }
  }, [tenantId, filters, pagination.page]);

  const fetchScheduledPublications = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...filters
      }).toString();

      const response = await apiClient(`/schedule/${tenantId}?${queryParams}`);
      
      if (response.success) {
        setScheduledPublications(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0
        }));
      } else {
        throw new Error(response.message || 'Failed to fetch scheduled publications');
      }
    } catch (error) {
      console.error('Error fetching scheduled publications:', error);
      toast({
        title: 'Error loading scheduled publications',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient(`/schedule/${tenantId}/stats`);
      if (response.success) {
        setStats(response.data || {});
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const cancelScheduledPublication = async (id) => {
    try {
      const response = await apiClient(`/schedule/${tenantId}/${id}/cancel`, {
        method: 'PUT'
      });

      if (response.success) {
        toast({
          title: 'Publication cancelled',
          description: 'The scheduled publication has been cancelled',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchScheduledPublications();
        fetchStats();
      } else {
        throw new Error(response.message || 'Failed to cancel publication');
      }
    } catch (error) {
      toast({
        title: 'Error cancelling publication',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCancellingId(null);
      onCancelDialogClose();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'blue';
      case 'completed': return 'green';
      case 'failed': return 'red';
      case 'cancelled': return 'gray';
      default: return 'gray';
    }
  };

  const getEntityTypeColor = (entityType) => {
    switch (entityType) {
      case 'page': return 'purple';
      case 'content_entry': return 'orange';
      case 'navigation': return 'teal';
      default: return 'gray';
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const confirmCancel = (id) => {
    setCancellingId(id);
    onCancelDialogOpen();
  };

  if (!tenantId) {
    return (
      <Container maxW="container.xl" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Scheduling</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Box>
            <Heading size="lg">Scheduled Publications</Heading>
            <Text color="gray.500">
              Manage and monitor your scheduled content publications
            </Text>
          </Box>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={() => navigate('/scheduling/create')}
          >
            Schedule Publication
          </Button>
        </HStack>

        {/* Stats Overview */}
        <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total Scheduled</StatLabel>
                <StatNumber>{stats.total_scheduled || 0}</StatNumber>
                <StatHelpText>
                  <HStack>
                    <TimeIcon color="blue.500" />
                    <Text>All time</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Pending</StatLabel>
                <StatNumber color="blue.500">{stats.pending || 0}</StatNumber>
                <StatHelpText>
                  <HStack>
                    <CalendarIcon color="blue.500" />
                    <Text>Awaiting publication</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Completed</StatLabel>
                <StatNumber color="green.500">{stats.completed || 0}</StatNumber>
                <StatHelpText>
                  <HStack>
                    <CheckCircleIcon color="green.500" />
                    <Text>Successfully published</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Success Rate</StatLabel>
                <StatNumber color="green.500">{stats.success_rate || 0}%</StatNumber>
                <StatHelpText>
                  <HStack>
                    <WarningIcon color={stats.success_rate >= 90 ? "green.500" : "orange.500"} />
                    <Text>Last 30 days</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </Grid>

        {/* Filters */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <HStack spacing={4} wrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement>
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search publications..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </InputGroup>

              <Select
                placeholder="All types"
                maxW="200px"
                value={filters.entity_type}
                onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
              >
                <option value="page">Pages</option>
                <option value="content_entry">Content Entries</option>
                <option value="navigation">Navigation</option>
              </Select>

              <Select
                placeholder="All statuses"
                maxW="200px"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </Select>

              <Button
                variant="outline"
                onClick={() => setFilters({ entity_type: '', status: '', search: '' })}
              >
                Clear Filters
              </Button>
            </HStack>
          </CardBody>
        </Card>

        {/* Publications Table */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardHeader>
            <Heading size="md">Scheduled Publications</Heading>
          </CardHeader>
          <CardBody>
            {loading ? (
              <Center py={8}>
                <Spinner size="lg" />
              </Center>
            ) : scheduledPublications.length > 0 ? (
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Entity</Th>
                      <Th>Type</Th>
                      <Th>Action</Th>
                      <Th>Scheduled For</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {scheduledPublications.map((publication) => (
                      <Tr key={publication.id}>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">
                              {publication.entity_name || `${publication.entity_type} #${publication.entity_id}`}
                            </Text>
                            {publication.version_id && (
                              <Text fontSize="sm" color="gray.500">
                                Version: {publication.version_id}
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={getEntityTypeColor(publication.entity_type)}>
                            {publication.entity_type.replace('_', ' ')}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge variant="outline">
                            {publication.action}
                          </Badge>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm">
                              {formatDateTime(publication.scheduled_at)}
                            </Text>
                            {publication.scheduled_at < new Date().toISOString() && publication.status === 'pending' && (
                              <Badge colorScheme="red" size="sm">Overdue</Badge>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(publication.status)}>
                            {publication.status}
                          </Badge>
                        </Td>
                        <Td>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<ChevronDownIcon />}
                              variant="outline"
                              size="sm"
                            />
                            <MenuList>
                              <MenuItem
                                icon={<EditIcon />}
                                onClick={() => navigate(`/scheduling/edit/${publication.id}`)}
                                isDisabled={publication.status !== 'pending'}
                              >
                                Edit
                              </MenuItem>
                              <MenuItem
                                icon={<DeleteIcon />}
                                onClick={() => confirmCancel(publication.id)}
                                isDisabled={publication.status !== 'pending'}
                                color="red.500"
                              >
                                Cancel
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <Center py={8}>
                <VStack spacing={4}>
                  <CalendarIcon boxSize={12} color="gray.400" />
                  <Text color="gray.500">No scheduled publications found</Text>
                  <Button
                    leftIcon={<AddIcon />}
                    colorScheme="blue"
                    onClick={() => navigate('/scheduling/create')}
                  >
                    Schedule Your First Publication
                  </Button>
                </VStack>
              </Center>
            )}
          </CardBody>
        </Card>

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <HStack justify="center" spacing={2}>
            <Button
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              isDisabled={pagination.page === 1}
            >
              Previous
            </Button>
            <Text fontSize="sm">
              Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
            </Text>
            <Button
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              isDisabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
            >
              Next
            </Button>
          </HStack>
        )}
      </VStack>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog
        isOpen={isCancelDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onCancelDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cancel Scheduled Publication
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to cancel this scheduled publication? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCancelDialogClose}>
                No, Keep It
              </Button>
              <Button 
                colorScheme="red" 
                onClick={() => cancelScheduledPublication(cancellingId)} 
                ml={3}
              >
                Yes, Cancel It
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default Scheduling;